import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverSetupBlueprint() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-setup-blueprint";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles
    ? articles.find((art) => art.slug === ref_id)
    : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    housing: "Housing Setup",
    transportation: "Transportation",
    utilities: "Utilities & Internet",
    workspaces: "Workspaces & Digital Nomad Life",
    banking: "Banking & Finances",
    healthcare: "Healthcare & Insurance",
    social: "Social Integration & Activities",
    locations: "Key Locations & Resources",
    regulations: "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/news-calendar/moving-to-vancouver.aspx",
      label: "City of Vancouver – Moving to Vancouver",
    },
    {
      url: "https://www.translink.ca/",
      label: "TransLink – Metro Vancouver Transit",
    },
    {
      url: "https://www.welcomebc.ca/Start-Your-Life-in-B-C",
      label: "WelcomeBC – Start Your Life in B.C.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <p> or <li> section. Coordinates go in `lat`/`lng`.
  const locations = [
    {
      id: "service-canada",
      lat: 49.28622,
      lng: -123.11357,
      popupText: "Service Canada Centre – Downtown Vancouver",
    },
    {
      id: "vpl",
      lat: 49.27979,
      lng: -123.11567,
      popupText: "Vancouver Public Library (Central Branch)",
    },
    {
      id: "waterfront",
      lat: 49.28583,
      lng: -123.11167,
      popupText: "Waterfront Station",
    },
    {
      id: "yvr",
      lat: 49.19333,
      lng: -123.17566,
      popupText: "Vancouver International Airport (YVR)",
    },
    {
      id: "city-hall",
      lat: 49.26083,
      lng: -123.11389,
      popupText: "Vancouver City Hall",
    },
    {
      id: "stanley-park",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park",
    },
    {
      id: "granville-island",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island Public Market",
    },
    {
      id: "canada-place",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "Canada Place & Vancouver Convention Centre",
    },
    {
      id: "issofbc",
      lat: 49.26105,
      lng: -123.06568,
      popupText: "ISSofBC Welcome Centre",
    },
    {
      id: "pacific-central",
      lat: 49.27361,
      lng: -123.09806,
      popupText: "Pacific Central Station",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver Setup Blueprint: Essential Tips for Newcomers & Digital Nomads</title>
        <meta
          name="description"
          content="A comprehensive guide to setting up life in Vancouver, covering housing, transportation, utilities, finances, workspaces, and more."
        />
        <meta
          name="keywords"
          content="Vancouver, newcomers, digital nomads, housing, transportation, banking, utilities"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-setup-blueprint.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver Setup Blueprint: Essential Tips for Newcomers & Digital Nomads"
        />
        <meta
          property="og:description"
          content="Learn how to find housing, get around, handle finances, connect socially, and thrive in Vancouver with this step-by-step guide."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-setup-blueprint.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-setup-blueprint.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver Setup Blueprint: Essential Tips for Newcomers & Digital Nomads"
        />
        <meta
          name="twitter:description"
          content="Navigate Vancouver like a pro—housing, transit, finances, social life, and more!"
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-setup-blueprint.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A scenic view of Vancouver skyline with mountains in the background"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver consistently ranks among the world’s most livable cities
              thanks to its natural beauty, diverse population, and robust
              infrastructure. Whether you’re a new immigrant, digital nomad, or
              relocating for work, Vancouver promises a high quality of life
              with year-round outdoor activities, vibrant neighborhoods, and
              modern amenities. This guide covers everything you need to get
              started—from housing and transportation to banking, utilities, and
              social integration.
            </p>

            {/* SECTION: Housing Setup */}
            <h2 id="housing">Housing Setup</h2>
            <p>
              Finding a place to call home is often the biggest challenge due to
              Vancouver’s competitive and pricey rental market. A one-bedroom in
              the city can easily cost over CAD $2,400 per month, and
              landlords typically require a damage deposit plus the first
              month’s rent upfront. Popular neighborhoods include Downtown,
              West End, Kitsilano, Mount Pleasant, and Commercial Drive, each
              offering its own unique vibe. Consider renting a short-term unit
              (e.g., Airbnb) while you apartment hunt in person to avoid scams.
              Make sure to familiarize yourself with British Columbia’s
              Residential Tenancy regulations to understand your rights and
              responsibilities.
            </p>

            {/* SECTION: Transportation */}
            <h2 id="transportation">Transportation</h2>
            <p>
              Vancouver’s efficient public transit system (TransLink) includes
              the SkyTrain, buses, and SeaBus, all of which use a unified payment
              method via the Compass Card. The city is also extremely
              bike-friendly, with an extensive network of protected lanes, and
              car-sharing services abound for those occasional trips requiring a
              vehicle. Walking is a pleasure in many neighborhoods, and rideshare
              services like Uber and Lyft make late-night rides easy.
            </p>

            {/* SECTION: Utilities & Internet */}
            <h2 id="utilities">Utilities & Internet</h2>
            <p>
              Setting up utilities is straightforward in Vancouver. BC Hydro
              handles electricity, while water/sewer/garbage are typically
              managed by the city (and often included in your rent). For internet
              and mobile services, TELUS and Shaw are the main providers.
              High-speed plans are widely available, and you can often find
              promotional deals. Make sure to also arrange a local mobile plan to
              avoid steep roaming charges.
            </p>

            {/* SECTION: Workspaces & Digital Nomad Life */}
            <h2 id="workspaces">Workspaces & Digital Nomad Life</h2>
            <p>
              Vancouver is a haven for remote workers, featuring numerous
              coworking spaces like WeWork, The Network Hub, and L’Atelier in
              Gastown. Coffee shops are abundant and welcoming to laptop users,
              particularly outside peak meal times. To connect with other digital
              nomads or tech folks, explore Meetup.com or local events. The city
              also boasts an array of public libraries and community centres
              offering free Wi-Fi and comfortable work areas.
            </p>

            {/* SECTION: Banking & Finances */}
            <h2 id="banking">Banking & Finances</h2>
            <p>
              Major Canadian banks (RBC, TD, Scotiabank, BMO, CIBC) have
              newcomer programs allowing you to open accounts without an
              established Canadian credit history. If you plan to work locally,
              you’ll need a Social Insurance Number (SIN) from Service Canada.
              Building your credit early is advisable, often starting with a
              basic or secured credit card. Vancouver’s cost of living can be
              high, so budgeting is essential.
            </p>

            {/* SECTION: Healthcare & Insurance */}
            <h2 id="healthcare">Healthcare & Insurance</h2>
            <p>
              British Columbia’s public healthcare system (MSP) requires
              enrollment and imposes a wait period of up to three months. During
              that time, or if you’re ineligible, private insurance is strongly
              recommended. MSP covers most medically necessary services, but not
              prescription drugs, dental, or vision. Walk-in clinics are common,
              although finding a family doctor can be challenging.
            </p>

            {/* SECTION: Social Integration & Activities */}
            <h2 id="social">Social Integration & Activities</h2>
            <p>
              Vancouverites can sometimes appear reserved, but the city offers
              plenty of ways to meet people—join sports leagues, attend local
              meetups, or explore cultural events and festivals. Volunteer
              opportunities abound, which is a great path to building a social
              circle. Take advantage of Vancouver’s famed outdoor activities:
              hiking, beach volleyball, kayaking, or strolling the scenic
              seawall. 
            </p>

            {/* SECTION: Key Locations & Resources */}
            <h2 id="locations">Key Locations & Resources</h2>
            <ul>
              <li
                ref={(el) => setSpotRef("service-canada", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Service Canada Centre – Downtown Vancouver</strong>{" "}
                <Marker
                  coordY={49.28622}
                  coordX={-123.11357}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Visit for government services like obtaining your SIN, at
                757 West Hastings St.
              </li>
              <li
                ref={(el) => setSpotRef("vpl", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver Public Library (Central Branch)</strong>{" "}
                <Marker
                  coordY={49.27979}
                  coordX={-123.11567}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Iconic building with free Wi-Fi, workspaces, and community events.
              </li>
              <li
                ref={(el) => setSpotRef("waterfront", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Waterfront Station</strong>{" "}
                <Marker
                  coordY={49.28583}
                  coordX={-123.11167}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Major transit hub connecting SkyTrain, SeaBus, and West Coast Express.
              </li>
              <li
                ref={(el) => setSpotRef("yvr", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver International Airport (YVR)</strong>{" "}
                <Marker
                  coordY={49.19333}
                  coordX={-123.17566}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Gateway for international and domestic flights, located in Richmond.
              </li>
              <li
                ref={(el) => setSpotRef("city-hall", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Vancouver City Hall</strong>{" "}
                <Marker
                  coordY={49.26083}
                  coordX={-123.11389}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Seat of municipal government, near Broadway–City Hall SkyTrain station.
              </li>
              <li
                ref={(el) => setSpotRef("stanley-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Stanley Park</strong>{" "}
                <Marker
                  coordY={49.3}
                  coordX={-123.14}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Famous 1,000-acre green space with seawall, beaches, and an aquarium.
              </li>
              <li
                ref={(el) => setSpotRef("granville-island", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Granville Island Public Market</strong>{" "}
                <Marker
                  coordY={49.27262}
                  coordX={-123.13524}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : A bustling center of fresh produce, artisan goods, and performing arts.
              </li>
              <li
                ref={(el) => setSpotRef("canada-place", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Canada Place & Vancouver Convention Centre</strong>{" "}
                <Marker
                  coordY={49.28880}
                  coordX={-123.11125}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Iconic waterfront area hosting major events, cruise ships, and attractions.
              </li>
              <li
                ref={(el) => setSpotRef("issofbc", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>ISSofBC Welcome Centre</strong>{" "}
                <Marker
                  coordY={49.26105}
                  coordX={-123.06568}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Immigrant Services Society hub with language classes and settlement help.
              </li>
              <li
                ref={(el) => setSpotRef("pacific-central", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Pacific Central Station</strong>{" "}
                <Marker
                  coordY={49.27361}
                  coordX={-123.09806}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                : Main intercity train & bus terminal for travel to Seattle or other Canadian cities.
              </li>
            </ul>

            {/* SECTION: Local Regulations & Best Practices */}
            <h2 id="regulations">Local Regulations & Best Practices</h2>
            <p>
              In British Columbia, tenants have strong protections under the
              Residential Tenancy Act, which sets limits on rent increases and
              requires proper notice for entry. Cycling without a helmet or
              smoking in restricted areas can result in fines. Public transit
              fares must be tapped in/out with a Compass Card, and driving
              requires switching to a BC driver’s license within 90 days if
              you’re settling long term. Vancouver also enforces strict recycling
              and composting bylaws and has specific laws about drinking alcohol
              and smoking cannabis in public.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Is Vancouver really that expensive to live in?</h3>
              <p>
                Yes, the housing cost is notably high, and everyday expenses
                (groceries, eating out) can add up. However, salaries in
                professional fields tend to be higher, and many people offset
                costs by using public transit, buying groceries strategically,
                and taking advantage of free or low-cost outdoor activities.
              </p>

              <h3>2. Do I need a car to get around?</h3>
              <p>
                Not necessarily. Vancouver’s transit system is robust, and many
                choose to go car-free, relying on SkyTrain, buses, bike lanes,
                and car-sharing services. If you live in central areas and
                occasionally need a vehicle for a day trip, services like Evo or
                Modo are great alternatives to full-time car ownership.
              </p>

              <h3>3. How do I get healthcare coverage when I arrive?</h3>
              <p>
                Enroll in MSP immediately if you’re eligible. It can take up to
                three months for coverage to begin, so get private insurance if
                needed. MSP covers most essential health services, but not
                prescriptions, dental, or vision, so consider extended benefits
                if needed.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Moving to Vancouver can be a major life change, but by understanding
              the city’s housing market, transportation options, financial system,
              and cultural norms, you can settle in smoothly. Take time to explore
              the city’s neighborhoods, partake in local activities, and build a
              network through volunteer or community events. Vancouver’s blend of
              urban excitement and spectacular natural settings will make your
              new life here both fulfilling and inspiring.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}